import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import CurrencyValue from '../CurrencyValue';

type Segment = {
    value: number;
    color: string;
};

type Props = {
    data: Segment[];
    label: string;
    isLoading?: boolean;
};

const AnalyticsDetailsChartSVG: FC<Props> = ({ data, label, isLoading = false }) => {
    const { t } = useTranslation();

    const { list, totalValue, listTotalValue } = useMemo(() => {
        const total = data.reduce((acc, segment) => acc + segment.value, 0);
        if (total === 0) {
            return {
                totalValue: 0,
                list: [{ value: 100, color: '#eaeaea' }],
                listTotalValue: 100,
            };
        }
        return { totalValue: total, list: data, listTotalValue: total };
    }, [data]);

    const segments = useMemo(() => {
        const radius = 110;
        const lineWidth = 15;
        const gapAngle = 0.2;
        const totalGapsAngle = gapAngle * list.length;
        const usableAngle = 2 * Math.PI - totalGapsAngle;

        let currentAngle = -Math.PI / 2;

        return list.map((segment, i) => {
            const segmentAngle = (segment.value / listTotalValue) * usableAngle;
            const startAngle = currentAngle;
            const endAngle = currentAngle + segmentAngle;
            currentAngle = endAngle + gapAngle;

            const cx = 120;
            const cy = 120;
            const startX = cx + radius * Math.cos(startAngle);
            const startY = cy + radius * Math.sin(startAngle);
            const endX = cx + radius * Math.cos(endAngle);
            const endY = cy + radius * Math.sin(endAngle);
            const largeArc = segmentAngle > Math.PI ? 1 : 0;

            return (
                <path
                    key={i}
                    d={`M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}`}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={lineWidth}
                    strokeLinecap="round"
                />
            );
        });
    }, [list, listTotalValue]);

    return (
        <div className="relative w-60 h-60">
            <svg
                viewBox="0 0 240 240"
                xmlns="http://www.w3.org/2000/svg"
                className={cn('w-full h-full', isLoading && 'animate-pulse')}
            >
                {segments}
            </svg>

            <div className="absolute inset-0 flex flex-col justify-center items-center">
                {isLoading ? (
                    <>
                        <div className="h-3 w-16 my-1.5 rounded-full bg-popover-100 animate-pulse" />
                        <div className="h-3 w-25 my-1 rounded-full bg-popover-100 animate-pulse" />
                    </>
                ) : (
                    <>
                        <span className="text-base font-medium">
                            <CurrencyValue value={totalValue} />
                        </span>
                        <span className="text-sm text-popover-600">{t(label)}</span>
                    </>
                )}
            </div>
        </div>
    );
};

export default AnalyticsDetailsChartSVG;
